import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Avatar } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { RegionAwareLinkComponent } from '@shared/components';
import { NymblTranslatePipe } from '@shared/pipes';
import {
  NotificationRow,
  alertTypeIcon,
  alertTypePanelLabel,
  alertTypeTagSeverity
} from '@core/models';
import { NotificationStore, V1_PATIENT_URL } from '@core/services';

/**
 * One notification inside the bell panel.
 *
 * Ports `app/views/_alert_entry.html`, which is a distinct template from the inbox page's
 * row — the panel is a stacked card with an avatar, an icon-led category, a timestamp and
 * the message body, where the inbox is a columnar table. The two are kept as separate
 * components rather than one with a layout flag because they carry different field sets and
 * different action sets: only the panel offers Create Task / Open Task, and only the inbox
 * page offers its Sent-filter variants.
 *
 * Action parity with v1's panel row, in v1's order:
 *
 * | v1 | Here |
 * |---|---|
 * | message text, `replyMessage(..., viewOnly: true)` | clicking the message opens View |
 * | `Create Task` / `Open Task` (`UserNotificationService.createTask`) | same, by task id |
 * | `Reply` | same |
 * | `Reply All` | same |
 * | `fa-eye` titled "Mark as Read", unread only | labelled button, unread only |
 * | `fa-trash` titled "Delete", read only | labelled button, read only |
 *
 * **Every action carries a visible label, unlike v1.** v1 renders mark-read and delete as
 * bare 10px Font Awesome glyphs whose only affordance is a `title` tooltip, which reads as
 * decoration rather than a control. The labels are a presentation change, not a behavior
 * change, so the port rule still holds.
 *
 * The avatar is initials, where v1 loads each sender's profile photo through its
 * `profile-photo` directive. The Read tab alone renders up to 30 rows, so photos would mean
 * up to 30 blob requests every time the panel opens; `p-avatar` sizes identically in label
 * and image mode, so swapping initials for photos later is a one-line change.
 *
 * Reads and writes through the shared store, so marking read here updates the inbox page's
 * list too if it is mounted behind the drawer.
 */
@Component({
  selector: 'app-notification-panel-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Avatar,
    ButtonModule,
    DatePipe,
    NymblTranslatePipe,
    RegionAwareLinkComponent,
    Tag
  ],
  host: {
    class:
      'block px-3 py-3 border-b border-surface-200 dark:border-surface-700 transition-colors hover:bg-emphasis'
  },
  template: `
    <div class="flex gap-3">
      <p-avatar
        shape="circle"
        [label]="initials()"
        [icon]="initials() ? undefined : 'pi pi-user'"
        styleClass="shrink-0"
      />

      <!-- min-w-0 lets the message wrap instead of forcing the row wider than the drawer. -->
      <div class="min-w-0 flex-1 flex flex-col gap-1">
        <div class="flex items-start justify-between gap-2">
          <span class="font-semibold text-sm leading-tight truncate">
            {{ senderLabel() }}
          </span>
          <p-tag
            [severity]="categorySeverity()"
            [icon]="categoryIcon()"
            [value]="categoryLabel() | nymblTranslate"
            styleClass="shrink-0 text-xs"
          />
        </div>

        <div class="flex items-center gap-1 text-xs text-muted-color">
          <span>{{ row().createdAt | date: 'MM/dd/yyyy h:mm a' }}</span>
          @if (row().patientId) {
            <span aria-hidden="true">&middot;</span>
            <app-region-aware-link
              path="{{ V1_PATIENT_URL }}/{{ row().patientId }}"
            >
              #{{ row().patientId }} {{ row().patientName }}
            </app-region-aware-link>
          }
        </div>

        <!--
          The message body is the View trigger, matching v1, where clicking it calls
          replyMessage with viewOnly: true (_alert_entry.html:24-25). It is a real button
          element rather than a span with role="button" so it is keyboard reachable, which
          v1's is not.
        -->
        <button
          type="button"
          class="text-sm text-left m-0 p-0 bg-transparent border-none cursor-pointer text-color hover:underline"
          [attr.aria-label]="
            ('notifications.actions.view' | nymblTranslate) +
            ': ' +
            row().message
          "
          (click)="viewRequested.emit(row())"
        >
          {{ row().message }}
        </button>

        <!--
          v1 stacks its actions as 10px text links, which fits four of them in the sidebar.
          p-button's default small padding does not, so the padding is trimmed here rather
          than dropping an action or letting the group wrap onto a second line.
        -->
        <div class="flex flex-wrap items-center gap-x-0.5 gap-y-0.5 pt-0.5">
          @if (!row().hasRead) {
            <p-button
              size="small"
              [text]="true"
              icon="pi pi-check"
              styleClass="!px-2 !py-1"
              [label]="'notifications.actions.mark_read' | nymblTranslate"
              (onClick)="markRead()"
            />
          }
          <p-button
            size="small"
            [text]="true"
            severity="secondary"
            icon="pi pi-reply"
            styleClass="!px-2 !py-1"
            [label]="'notifications.actions.reply' | nymblTranslate"
            (onClick)="replyRequested.emit(row())"
          />
          <p-button
            size="small"
            [text]="true"
            severity="secondary"
            icon="pi pi-share-alt"
            styleClass="!px-2 !py-1"
            [label]="'notifications.actions.reply_all' | nymblTranslate"
            (onClick)="replyAllRequested.emit(row())"
          />
          <p-button
            size="small"
            [text]="true"
            severity="secondary"
            icon="pi pi-check-square"
            styleClass="!px-2 !py-1"
            [label]="taskLabel() | nymblTranslate"
            (onClick)="taskRequested.emit(row())"
          />
          @if (row().hasRead) {
            <p-button
              size="small"
              [text]="true"
              severity="danger"
              icon="pi pi-trash"
              styleClass="!px-2 !py-1"
              [label]="'notifications.actions.delete' | nymblTranslate"
              (onClick)="remove()"
            />
          }
        </div>
      </div>
    </div>
  `
})
export class NotificationPanelRow {
  private store = inject(NotificationStore);

  readonly row = input.required<NotificationRow>();

  /**
   * View / Reply / Reply All and the task link are raised rather than handled here, because
   * all four need something only the panel owns: the one compose dialog instance, and closing
   * the drawer before navigating away from it.
   */
  readonly viewRequested = output<NotificationRow>();
  readonly replyRequested = output<NotificationRow>();
  readonly replyAllRequested = output<NotificationRow>();
  readonly taskRequested = output<NotificationRow>();

  protected readonly V1_PATIENT_URL = V1_PATIENT_URL;

  protected readonly categoryLabel = computed(() =>
    alertTypePanelLabel(this.row().alertType)
  );
  protected readonly categoryIcon = computed(() =>
    alertTypeIcon(this.row().alertType)
  );
  protected readonly categorySeverity = computed(() =>
    alertTypeTagSeverity(this.row().alertType)
  );

  /** v1 prints an empty prefix for a sender it cannot resolve; a dash reads as absent. */
  protected readonly senderLabel = computed(() => this.row().senderName ?? '—');

  /**
   * First letter of the first and last word of the sender's name, so "Bradley Moore" gives
   * "BM". Empty for an unresolved sender, which makes `p-avatar` fall back to its user icon.
   */
  protected readonly initials = computed(() => {
    const parts = (this.row().senderName ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length === 0) return '';
    const first = parts[0][0];
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  });

  /** v1 swaps the same control's label on `ng-hide="un.taskId"` (`_alert_entry.html:34-39`). */
  protected readonly taskLabel = computed(() =>
    this.row().taskId
      ? ('notifications.actions.open_task' as const)
      : ('notifications.actions.create_task' as const)
  );

  protected markRead(): void {
    this.store.markRead(this.row());
  }

  protected remove(): void {
    this.store.remove(this.row());
  }
}
